import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Icon } from '../tokens';
import Announcements from './admin/Announcements';
import './Admin.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ISO (UTC) → valor de un <input type="datetime-local">, que espera hora
// local sin zona. Devuelve '' si no hay fecha.
const toLocalInput = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EMPTY_LISTING = {
  name: '', brand: '', category: '', material: '', scale: '',
  sku: '', toyNumber: '', launchYear: '', description: '',
  marketValueAmount: '', carded: false,
};

export default function Admin() {
  const { user, loading, listWaitlist, sendWaitlistInvite, listAllInvites,
    listSellerRequests, approveSellerRequest, rejectSellerRequest,
    listAllUsers, grantUserInvites, getAdminMetrics, listEmailLogs } = useAuth();
  const { t } = useTranslation();
  const [entries, setEntries] = useState([]);
  const [invites, setInvites] = useState([]);
  // Catálogo: paginado en el servidor. Con 14k+ piezas, traerlas todas eran
  // decenas de MB de JSON y 14k <img> en el DOM en cada carga del panel.
  const [catalog, setCatalog] = useState([]);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [catPage, setCatPage] = useState(1);
  const [catPages, setCatPages] = useState(1);
  const [catQuery, setCatQuery] = useState('');   // texto aplicado
  const [catInput, setCatInput] = useState('');   // texto que se está escribiendo
  const [catLoading, setCatLoading] = useState(false);
  const [publications, setPublications] = useState([]);
  const [sellerReqs, setSellerReqs] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [raffle, setRaffle] = useState([]); // participantes del sorteo (todas las rondas)
  const [raffleRound, setRaffleRound] = useState(null); // ronda visible (null = vigente)
  const [metrics, setMetrics] = useState(null);
  const [options, setOptions] = useState({ brands: [], categories: [], materials: [], scales: [] });
  const [fetching, setFetching] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  const [grantingId, setGrantingId] = useState(null);
  const [flash, setFlash] = useState({ type: '', msg: '' });
  const [tab, setTab] = useState('waitlist');
  // Tab «Correos»: histórico de envíos con su estado de entrega (lo llena el
  // webhook de Resend). Se carga aparte del refresh general porque solo hace
  // falta cuando se abre el tab.
  const [mail, setMail] = useState(null); // { logs, total, resumen, tipos, webhookConfigurado }
  const [mailFilter, setMailFilter] = useState('');
  const [mailLoading, setMailLoading] = useState(false);

  // Configuración del Home (toggle de fuente + ventana de piezas).
  const [settings, setSettings] = useState(null); // { homeSource, homeStart, homeEnd, homeLimit, counts }
  // Borrador de la ventana del Home: { from, to } 1-based en texto.
  // null = sin cambios locales (se muestra lo persistido).
  const [winDraft, setWinDraft] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  // Catalog editor state
  const [editing, setEditing] = useState(null); // null | 'new' | listing/pub object
  const [editMode, setEditMode] = useState('listing'); // 'listing' | 'publication' | 'promote'
  const [sourcePubId, setSourcePubId] = useState(null); // pub origen (promote/edit pub)
  const [form, setForm] = useState(EMPTY_LISTING);
  const [imgFile, setImgFile] = useState(null);
  const [removeImg, setRemoveImg] = useState(false);
  const [savingCat, setSavingCat] = useState(false);

  const loadMail = useCallback(async (delivery = '') => {
    setMailLoading(true);
    try {
      setMail(await listEmailLogs(delivery ? { delivery, limit: 200 } : { limit: 200 }));
    } catch (err) {
      setFlash({ type: 'err', msg: err.response?.data?.message || 'No se pudo cargar el registro de correos' });
    } finally { setMailLoading(false); }
  }, [listEmailLogs]);

  useEffect(() => { if (tab === 'mail') loadMail(mailFilter); }, [tab, mailFilter, loadMail]);

  // Una página del catálogo. Va por separado del refresh general para que
  // paginar o buscar no vuelva a pedir waitlist, usuarios, métricas, etc.
  const CAT_PAGE_SIZE = 50;
  const loadCatalog = useCallback(async (page = 1, search = '') => {
    setCatLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/listings/admin/all`, {
        params: { page, limit: CAT_PAGE_SIZE, search: search || undefined },
      });
      setCatalog(data.listings || []);
      setCatalogTotal(data.total || 0);
      setCatPages(data.pages || 1);
    } catch (err) {
      setFlash({ type: 'err', msg: err.response?.data?.message || t('admin.err_load') });
    } finally { setCatLoading(false); }
  }, [t]);

  const refresh = useCallback(async () => {
    setFetching(true);
    try {
      // El recálculo de ownersCount ya NO corre aquí: recorría todo el catálogo
      // en serie antes de cualquier otra query. Los contadores se mantienen al
      // mutar publicaciones; para repararlos está el botón del tab «Panel».
      const [w, m, o, sr, au, mt, pb, st, rf] = await Promise.all([
        listWaitlist(),
        listAllInvites(),
        axios.get(`${API}/api/listings/options`).then(r => r.data),
        listSellerRequests('all').catch(() => []),
        listAllUsers().catch(() => []),
        getAdminMetrics().catch(() => null),
        axios.get(`${API}/api/publications?unlinked=true&limit=500`).then(r => r.data).catch(() => ({ publications: [] })),
        axios.get(`${API}/api/settings`).then(r => r.data).catch(() => null),
        axios.get(`${API}/api/users/admin/raffle-participants`).then(r => r.data).catch(() => ({ participants: [] })),
      ]);
      setEntries(w.entries);
      setInvites(m.invites);
      setOptions(o);
      setSellerReqs(sr || []);
      setAllUsers(au || []);
      setMetrics(mt);
      setPublications(pb.publications || []);
      setSettings(st);
      setRaffle(rf.participants || []);
    } catch (err) {
      setFlash({ type: 'err', msg: err.response?.data?.message || t('admin.err_load') });
    } finally {
      setFetching(false);
    }
  }, [listWaitlist, listAllInvites, listSellerRequests, listAllUsers, getAdminMetrics, t]);

  const handleApproveSeller = async (id) => {
    try {
      await approveSellerRequest(id);
      setFlash({ type: 'ok', msg: 'Vendedor aprobado · 999 invitaciones asignadas' });
      refresh();
    } catch (err) {
      setFlash({ type: 'err', msg: err.response?.data?.message || 'Error aprobando vendedor' });
    }
  };
  const handleRejectSeller = async (id) => {
    try {
      await rejectSellerRequest(id);
      setFlash({ type: 'ok', msg: 'Solicitud rechazada' });
      refresh();
    } catch (err) {
      setFlash({ type: 'err', msg: err.response?.data?.message || 'Error rechazando solicitud' });
    }
  };

  const handleGrantInvites = async (target) => {
    setGrantingId(target._id);
    setFlash({ type: '', msg: '' });
    try {
      const { granted, user: updated } = await grantUserInvites(target._id, 30);
      setAllUsers(prev => prev.map(u => u._id === target._id ? { ...u, invitesLeft: updated.invitesLeft } : u));
      setFlash({ type: 'ok', msg: `+${granted} invitaciones para ${target.username || target.name || target.email}` });
    } catch (err) {
      setFlash({ type: 'err', msg: err.response?.data?.message || 'Error otorgando invitaciones' });
    } finally {
      setGrantingId(null);
    }
  };

  useEffect(() => { if (user?.role === 'admin') refresh(); }, [user, refresh]);
  // El catálogo se recarga solo cuando cambia la página o la búsqueda.
  useEffect(() => {
    if (user?.role === 'admin') loadCatalog(catPage, catQuery);
  }, [user, loadCatalog, catPage, catQuery]);

  // ── Configuración del Home ─────────────────────────────────
  const patchSettings = async (patch) => {
    setSavingSettings(true);
    setFlash({ type: '', msg: '' });
    try {
      const { data } = await axios.patch(`${API}/api/settings`, patch);
      setSettings(data);
      setFlash({ type: 'ok', msg: 'Configuración guardada' });
      return true;
    } catch (err) {
      setFlash({ type: 'err', msg: err.response?.data?.message || 'Error al guardar configuración' });
      refresh(); // revierte el cambio optimista
      return false;
    } finally { setSavingSettings(false); }
  };
  const toggleHomeSource = () => {
    const next = settings?.homeSource === 'publications' ? 'catalog' : 'publications';
    setSettings((s) => ({ ...s, homeSource: next }));
    patchSettings({ homeSource: next });
  };
  // Piezas en venta de todos los usuarios, antepuestas a la ventana del Home.
  const toggleHomeSales = () => {
    const next = settings?.homeShowSales !== true;
    setSettings((s) => ({ ...s, homeShowSales: next }));
    patchSettings({ homeShowSales: next });
  };
  // Dinámica de growth "Participar en el sorteo": muestra u oculta el botón
  // del sorteo en los perfiles (user / user_premium).
  const toggleGrowthRaffle = () => {
    const next = !(settings?.growthRaffleEnabled !== false);
    setSettings((s) => ({ ...s, growthRaffleEnabled: next }));
    patchSettings({ growthRaffleEnabled: next });
  };
  // Insignia "Eres miembro #N de Troly": muestra u oculta el chip de número
  // de miembro en el perfil de todos los usuarios.
  const toggleMemberChip = () => {
    const next = !(settings?.memberChipEnabled !== false);
    setSettings((s) => ({ ...s, memberChipEnabled: next }));
    patchSettings({ memberChipEnabled: next });
  };
  // Comisión de Troly en ventas cobradas con Stripe Connect. Solo aplica a
  // checkouts nuevos: cada orden congela la comisión vigente al crearse.
  const toggleCommission = () => {
    const next = settings?.marketplaceCommissionEnabled !== true;
    setSettings((s) => ({ ...s, marketplaceCommissionEnabled: next }));
    patchSettings({ marketplaceCommissionEnabled: next });
  };
  const commitCommissionPct = (value) => {
    const n = Math.round(Number(value) * 100) / 100;
    if (!Number.isFinite(n) || n === settings?.marketplaceCommissionPct) return;
    const next = Math.max(0, Math.min(50, n));
    setSettings((s) => ({ ...s, marketplaceCommissionPct: next }));
    patchSettings({ marketplaceCommissionPct: next });
  };
  // Territorio del sorteo: persiste al salir del campo o presionar Enter.
  // Vacío = sorteo sin restricción territorial.
  const commitTerritorio = (value) => {
    const next = String(value ?? '').trim();
    if (next === (settings?.sorteoTerritorio ?? '')) return;
    setSettings((s) => ({ ...s, sorteoTerritorio: next }));
    patchSettings({ sorteoTerritorio: next });
  };
  // Cierre de inscripciones de la ronda vigente. El input es datetime-local
  // (hora del navegador del admin) y se persiste en ISO. Vacío = sin cierre.
  const commitCierre = (value) => {
    const next = value ? new Date(value).toISOString() : null;
    const prev = settings?.sorteoCierraAt ? new Date(settings.sorteoCierraAt).toISOString() : null;
    if (next === prev) return;
    setSettings((s) => ({ ...s, sorteoCierraAt: next }));
    patchSettings({ sorteoCierraAt: next });
  };
  // Cierra la ronda vigente del sorteo y arranca la siguiente: los
  // participantes quedan archivados y todos deben cumplir las condiciones
  // de nuevo (4 referidos y 4 piezas nuevos) para entrar a la nueva ronda.
  const startNewRaffleRound = () => {
    const current = settings?.sorteoRonda || 1;
    const ok = window.confirm(
      `¿Cerrar el sorteo ${current} e iniciar el sorteo ${current + 1}?\n\n` +
      `Los participantes del sorteo ${current} quedan archivados. Para entrar al ` +
      `sorteo ${current + 1}, cada usuario deberá sumar 4 referidos y 4 piezas nuevos.`
    );
    if (!ok) return;
    setSettings((s) => ({ ...s, sorteoRonda: current + 1 }));
    setRaffleRound(null);
    patchSettings({ sorteoRonda: current + 1 });
  };
  // Ventana [homeStart, homeEnd) del Home. La UI la muestra 1-based
  // (inicio–fin) y se edita en borrador: el slider y los dos campos escriben
  // aquí, y nada se persiste hasta que el admin presiona «Guardar».
  const winSaved = (s) => {
    const start = Number(s?.homeStart ?? 0);
    const end = Number(s?.homeEnd ?? s?.homeLimit ?? 80);
    return { from: String(start + 1), to: String(end) };
  };
  // Guardamos texto (no números) para no pelear con el campo mientras se
  // escribe: borrarlo entero no debe convertirlo en 0 de inmediato.
  const winEdit = winDraft ?? winSaved(settings);
  // Texto 1-based → posiciones internas 0-based.
  const winNums = (w) => {
    const from = Math.max(1, parseInt(w.from, 10) || 1);
    const to = Math.max(0, parseInt(w.to, 10) || 0);
    return { start: from - 1, end: to };
  };
  const winPos = winNums(winEdit);
  const winDirty = !!winDraft && !!settings && (() => {
    const saved = winNums(winSaved(settings));
    return saved.start !== winPos.start || saved.end !== winPos.end;
  })();
  // Campos de texto: edición libre; se normalizan al guardar.
  const setWinField = (k) => (v) => setWinDraft({ ...winEdit, [k]: v });
  // Manijas del slider: se respetan entre sí para que no se crucen.
  const onStartInput = (v) => setWinDraft({ ...winEdit, from: String(Math.min(Number(v), winPos.end) + 1) });
  const onEndInput = (v) => setWinDraft({ ...winEdit, to: String(Math.max(Number(v), winPos.start)) });
  const saveWindow = async () => {
    const { start, end } = winPos;
    const ok = await patchSettings({ homeStart: start, homeEnd: Math.max(start, end) });
    // Solo al guardar bien soltamos el borrador: si falló, no perdemos lo escrito.
    if (ok) setWinDraft(null); // el servidor manda: refleja lo persistido (ya acotado)
  };
  const discardWindow = () => setWinDraft(null);

  // Recálculos de mantenimiento del catálogo (antes corrían solos en cada
  // carga del panel y bloqueaban todo lo demás). `maintaining` guarda el
  // endpoint en curso para deshabilitar ambos botones.
  const [maintaining, setMaintaining] = useState(null);
  const runMaintenance = async (endpoint, label) => {
    setMaintaining(endpoint);
    setFlash({ type: '', msg: '' });
    try {
      const { data } = await axios.post(`${API}/api/listings/${endpoint}`);
      setFlash({ type: 'ok', msg: `${label}: ${data.updated}/${data.total} piezas actualizadas` });
      refresh();
      loadCatalog(catPage, catQuery);
    } catch (err) {
      setFlash({ type: 'err', msg: err.response?.data?.message || `Error recalculando ${label}` });
    } finally { setMaintaining(null); }
  };

  const inviteStatus = (inv) => {
    if (inv.usedBy) return { label: t('admin.pill_used'), cls: 'pill--done' };
    if (new Date(inv.expiresAt) < new Date()) return { label: t('admin.pill_expired'), cls: 'pill--wait' };
    if (inv.emailStatus === 'sent') return { label: t('admin.pill_sent_pending'), cls: 'pill--ok' };
    if (inv.emailStatus === 'failed') return { label: t('admin.pill_send_failed'), cls: 'pill--err' };
    return { label: t('admin.pill_code_only'), cls: 'pill--wait' };
  };

  const handleSend = async (entry) => {
    setSendingId(entry._id);
    setFlash({ type: '', msg: '' });
    try {
      await sendWaitlistInvite(entry.email);
      setFlash({ type: 'ok', msg: `${t('admin.sent_to')} ${entry.email}` });
      setEntries(prev => prev.map(e => e._id === entry._id ? { ...e, sent: true } : e));
    } catch (err) {
      setFlash({ type: 'err', msg: err.response?.data?.message || t('admin.err_send') });
    } finally {
      setSendingId(null);
    }
  };

  // ── Catalog handlers ───────────────────────────────────────
  // Pasa los campos comunes (listing o publicación) al formulario del editor.
  const prefillFrom = (src) => setForm({
    name: src.name || '', brand: src.brand || '', category: src.category || '',
    material: src.material || '', scale: src.scale || '', sku: src.sku || '',
    toyNumber: src.toyNumber || '', launchYear: src.launchYear || '',
    description: src.description || '',
    marketValueAmount: src.marketValue?.amount ?? '',
    carded: !!src.carded,
  });

  const openEdit = (listing) => {
    setEditMode('listing'); setSourcePubId(null);
    prefillFrom(listing);
    setImgFile(null); setRemoveImg(false);
    setEditing(listing);
  };
  // Editar una publicación de un usuario (admin).
  const openEditPub = (pub) => {
    setEditMode('publication'); setSourcePubId(pub._id);
    prefillFrom(pub);
    setImgFile(null); setRemoveImg(false);
    setEditing(pub);
  };
  // Promover una publicación al catálogo: abre el editor pre-llenado.
  const openPromote = (pub) => {
    setEditMode('promote'); setSourcePubId(pub._id);
    prefillFrom(pub);
    setImgFile(null); setRemoveImg(false);
    setEditing('new');
  };
  const closeEditor = () => {
    setEditing(null); setEditMode('listing'); setSourcePubId(null);
    setForm(EMPTY_LISTING); setImgFile(null); setRemoveImg(false);
  };

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const saveCatalog = async () => {
    setSavingCat(true); setFlash({ type: '', msg: '' });
    try {
      const fd = new FormData();
      // `carded` va aparte: es booleano y hay que mandar también el false.
      Object.entries(form).forEach(([k, v]) => { if (k !== 'carded' && v) fd.append(k, v); });
      if (editMode !== 'publication') fd.append('carded', form.carded ? 'true' : 'false');

      if (editMode === 'publication') {
        // Editar publicación: las imágenes del usuario van en el campo 'images'.
        if (imgFile) fd.append('images', imgFile);
        await axios.patch(`${API}/api/publications/${sourcePubId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setFlash({ type: 'ok', msg: t('admin.pub_updated', { defaultValue: 'Publicación actualizada' }) });
      } else if (editMode === 'promote') {
        if (imgFile) fd.append('image', imgFile);
        fd.append('fromPublication', sourcePubId);
        await axios.post(`${API}/api/listings`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setFlash({ type: 'ok', msg: t('admin.cat_promoted', { defaultValue: 'Pieza agregada al catálogo' }) });
      } else {
        if (imgFile) fd.append('image', imgFile);
        else if (removeImg && editing !== 'new') fd.append('clearImage', 'true');
        if (editing === 'new') {
          await axios.post(`${API}/api/listings`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          setFlash({ type: 'ok', msg: t('admin.cat_created', { defaultValue: 'Pieza creada en catálogo' }) });
        } else {
          await axios.patch(`${API}/api/listings/${editing._id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          setFlash({ type: 'ok', msg: t('admin.cat_updated', { defaultValue: 'Pieza actualizada' }) });
        }
      }
      closeEditor();
      refresh();
      loadCatalog(catPage, catQuery);
    } catch (err) {
      setFlash({ type: 'err', msg: err.response?.data?.message || t('admin.cat_err_save', { defaultValue: 'Error al guardar' }) });
    } finally { setSavingCat(false); }
  };

  const deleteCatalog = async (listing) => {
    if (!window.confirm(t('admin.cat_confirm_delete', { defaultValue: `Borrar "${listing.name}" del catálogo?` }))) return;
    try {
      await axios.delete(`${API}/api/listings/${listing._id}`);
      setFlash({ type: 'ok', msg: t('admin.cat_deleted', { defaultValue: 'Pieza borrada' }) });
      refresh();
      loadCatalog(catPage, catQuery);
    } catch (err) {
      setFlash({ type: 'err', msg: err.response?.data?.message || t('admin.cat_err_delete', { defaultValue: 'Error al borrar' }) });
    }
  };

  const deletePublication = async (pub) => {
    if (!window.confirm(t('admin.pub_confirm_delete', { defaultValue: `¿Borrar la publicación "${pub.name}"? Se elimina de la base de datos.` }))) return;
    try {
      await axios.delete(`${API}/api/publications/${pub._id}`);
      setFlash({ type: 'ok', msg: t('admin.pub_deleted', { defaultValue: 'Publicación borrada' }) });
      refresh();
    } catch (err) {
      setFlash({ type: 'err', msg: err.response?.data?.message || t('admin.cat_err_delete', { defaultValue: 'Error al borrar' }) });
    }
  };

  if (loading) return <div className="admin-loading">{t('admin.loading')}</div>;
  if (!user) return <Navigate to="/trigger" replace />;
  if (user.role !== 'admin') return <Navigate to="/profile" replace />;

  const pending = entries.filter(e => !e.invited);
  const sellersPending = sellerReqs.filter((r) => r.sellerStatus === 'pending').length;
  const sellersActive = sellerReqs.filter((r) => r.sellerStatus === 'approved').length;
  const usersActive = allUsers.filter((u) => !u.deletedAt).length;
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
  const fmtMoney = (n) => (n == null || isNaN(n)) ? '—' : new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(n);

  return (
    <div className="admin">
      <nav className="admin-nav">
        <Link to="/profile" className="admin-nav__back">{Icon.chevronDown(14)} {t('admin.back')}</Link>
        <span className="admin-nav__brand">{t('admin.brand')}</span>
        <span className="admin-nav__role">{user.email}</span>
      </nav>

      <div className="admin-body">
        <header className="admin-head">
          <h1>{t('admin.title')}</h1>
          <p>{t('admin.subtitle_p1')} <strong>support@troly.club</strong>.</p>
          <div className="admin-stats">
            <div className="admin-stat"><span>{t('admin.stat_total')}</span><strong>{entries.length}</strong></div>
            <div className="admin-stat"><span>{t('admin.stat_pending')}</span><strong>{pending.length}</strong></div>
            <div className="admin-stat"><span>{t('admin.stat_invites_sent')}</span><strong>{invites.length}</strong></div>
            <div className="admin-stat"><span>{t('admin.cat_total', { defaultValue: 'Catálogo' })}</span><strong>{catalogTotal}</strong></div>
          </div>
        </header>

        {flash.msg && (
          <div className={`admin-flash admin-flash--${flash.type}`}>{flash.msg}</div>
        )}

        <div className="admin-tabs">
          <button className={`admin-tab${tab==='waitlist'?' admin-tab--active':''}`} onClick={() => setTab('waitlist')}>
            {t('admin.tab_waitlist')} ({entries.length})
          </button>
          <button className={`admin-tab${tab==='invites'?' admin-tab--active':''}`} onClick={() => setTab('invites')}>
            {t('admin.tab_invites')} ({invites.length})
          </button>
          <button className={`admin-tab${tab==='catalog'?' admin-tab--active':''}`} onClick={() => setTab('catalog')}>
            {t('admin.tab_catalog', { defaultValue: 'Catálogo' })} ({catalogTotal})
          </button>
          <button className={`admin-tab${tab==='publications'?' admin-tab--active':''}`} onClick={() => setTab('publications')}>
            {t('admin.tab_publications', { defaultValue: 'Publicaciones' })} ({publications.length})
          </button>
          <button className={`admin-tab${tab==='sellers'?' admin-tab--active':''}`} onClick={() => setTab('sellers')}>
            Vendedores ({sellersPending} · {sellersActive} activos)
          </button>
          <button className={`admin-tab${tab==='users'?' admin-tab--active':''}`} onClick={() => setTab('users')}>
            User ({usersActive})
          </button>
          <button className={`admin-tab${tab==='raffle'?' admin-tab--active':''}`} onClick={() => setTab('raffle')}>
            Sorteo ({raffle.filter((p) => p.ronda === (settings?.sorteoRonda || 1)).length})
          </button>
          <button className={`admin-tab${tab==='metrics'?' admin-tab--active':''}`} onClick={() => setTab('metrics')}>
            Métricas
          </button>
          <button className={`admin-tab${tab==='announcements'?' admin-tab--active':''}`} onClick={() => setTab('announcements')}>
            Anuncios
          </button>
          <button className={`admin-tab${tab==='mail'?' admin-tab--active':''}`} onClick={() => setTab('mail')}>
            Correos{mail?.resumen ? ` (${(mail.resumen.bounced || 0) + (mail.resumen.complained || 0)} con problema)` : ''}
          </button>
          <button className={`admin-tab${tab==='panel'?' admin-tab--active':''}`} onClick={() => setTab('panel')}>
            Panel
          </button>
          <button className="btn-ghost" style={{ marginLeft: 'auto' }} onClick={refresh} disabled={fetching}>
            {fetching ? t('admin.refreshing') : t('admin.refresh')}
          </button>
        </div>

        {tab === 'waitlist' && (
          fetching && entries.length === 0 ? (
            <div className="admin-empty">{t('admin.loading_waitlist')}</div>
          ) : entries.length === 0 ? (
            <div className="admin-empty">{t('admin.empty_waitlist')}</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>#</th>
                  <th>{t('admin.th_email')}</th>
                  <th style={{ width: 160 }}>{t('admin.th_registered')}</th>
                  <th style={{ width: 160 }}>{t('admin.th_status')}</th>
                  <th style={{ width: 180 }}></th>
                </tr>
              </thead>
              <tbody>
                {entries.map(entry => (
                  <tr key={entry._id}>
                    <td>#{entry.position}</td>
                    <td>{entry.email}</td>
                    <td>{new Date(entry.createdAt).toLocaleDateString()}</td>
                    <td>
                      {entry.invited ? (
                        <span className="pill pill--done">{t('admin.pill_registered')}</span>
                      ) : entry.sent ? (
                        <span className="pill pill--ok">{t('admin.pill_code_sent')}</span>
                      ) : (
                        <span className="pill pill--wait">{t('admin.pill_waiting')}</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {!entry.invited && (
                        <button
                          className="btn-teal btn-sm"
                          onClick={() => handleSend(entry)}
                          disabled={sendingId === entry._id}
                        >
                          {sendingId === entry._id ? t('admin.btn_sending') : entry.sent ? t('admin.btn_resend') : t('admin.btn_send_code')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {tab === 'invites' && (
          fetching && invites.length === 0 ? (
            <div className="admin-empty">{t('admin.loading_invites')}</div>
          ) : invites.length === 0 ? (
            <div className="admin-empty">{t('admin.empty_invites')}</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 120 }}>{t('admin.th_code')}</th>
                  <th>{t('admin.th_sent_to')}</th>
                  <th style={{ width: 160 }}>{t('admin.th_sent_by', { defaultValue: 'Enviada por' })}</th>
                  <th style={{ width: 130 }}>{t('admin.th_created')}</th>
                  <th style={{ width: 180 }}>{t('admin.th_status')}</th>
                  <th>{t('admin.th_used_by')}</th>
                </tr>
              </thead>
              <tbody>
                {invites.map(inv => {
                  const st = inviteStatus(inv);
                  const sender = inv.createdBy;
                  const senderName = sender
                    ? (sender.username || sender.name || sender.email || '—')
                    : '—';
                  return (
                    <tr key={inv._id}>
                      <td style={{ fontFamily:'monospace', fontWeight:600, color:'var(--teal)' }}>
                        {inv.code.slice(0,3)}-{inv.code.slice(3)}
                      </td>
                      <td>{inv.sentTo || <span style={{ color:'var(--muted)' }}>{t('admin.not_sent')}</span>}</td>
                      <td>
                        {sender ? (
                          <div>
                            <div style={{ fontWeight: 500 }}>{senderName}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em' }}>
                              {sender.role || 'user'}
                            </div>
                          </div>
                        ) : (
                          <span style={{ color: 'var(--muted)' }}>—</span>
                        )}
                      </td>
                      <td>{new Date(inv.createdAt).toLocaleDateString()}</td>
                      <td><span className={`pill ${st.cls}`}>{st.label}</span></td>
                      <td>{inv.usedBy ? (inv.usedBy.name || inv.usedBy.username || inv.usedBy.email) : <span style={{ color:'var(--muted)' }}>—</span>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}

        {tab === 'catalog' && (
          <>
            {/* Buscador servidor-side: filtra por nombre, SKU o toy number sin
                traer el catálogo entero al navegador. */}
            <form
              className="admin-catbar"
              onSubmit={(e) => { e.preventDefault(); setCatPage(1); setCatQuery(catInput.trim()); }}
            >
              <input
                type="search"
                className="light-input"
                placeholder={t('admin.cat_search', { defaultValue: 'Buscar por nombre, SKU o toy number…' })}
                value={catInput}
                onChange={(e) => setCatInput(e.target.value)}
              />
              <button type="submit" className="btn-teal btn-sm" disabled={catLoading}>
                {catLoading ? '…' : t('admin.cat_search_btn', { defaultValue: 'Buscar' })}
              </button>
              {catQuery && (
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => { setCatInput(''); setCatPage(1); setCatQuery(''); }}
                >
                  {t('admin.cat_search_clear', { defaultValue: 'Limpiar' })}
                </button>
              )}
              <span className="admin-catbar__count">
                {catalogTotal} {t('admin.cat_pieces', { defaultValue: 'piezas' })}
              </span>
            </form>

            {catLoading && catalog.length === 0 ? (
              <div className="admin-empty">{t('admin.cat_loading', { defaultValue: 'Cargando catálogo…' })}</div>
            ) : catalog.length === 0 ? (
              <div className="admin-empty">
                {catQuery
                  ? t('admin.cat_no_results', { defaultValue: 'Ninguna pieza coincide con la búsqueda.' })
                  : t('admin.cat_empty', { defaultValue: 'El catálogo está vacío.' })}
              </div>
            ) : (
              <table className="admin-table">
                <thead>
                  <tr>
                    <th style={{ width: 64 }}></th>
                    <th>{t('admin.cat_th_name', { defaultValue: 'Pieza' })}</th>
                    <th style={{ width: 130 }}>{t('admin.cat_th_brand', { defaultValue: 'Marca' })}</th>
                    <th style={{ width: 90 }}>{t('admin.cat_th_sku', { defaultValue: 'SKU' })}</th>
                    <th style={{ width: 80 }}>{t('admin.cat_th_scale', { defaultValue: 'Escala' })}</th>
                    <th style={{ width: 70 }}>{t('admin.cat_th_owners', { defaultValue: 'Dueños' })}</th>
                    <th style={{ width: 160 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {catalog.map((l) => (
                    <tr key={l._id}>
                      <td>
                        {(l.cover || l.thumbnail || l.image) ? (
                          // loading="lazy" + medidas explícitas: el navegador solo
                          // descarga las miniaturas de las filas visibles.
                          <img
                            src={l.cover || l.thumbnail || l.image}
                            alt=""
                            width={48}
                            height={48}
                            loading="lazy"
                            decoding="async"
                            style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6 }}
                          />
                        ) : <div style={{ width: 48, height: 48, background: 'var(--bg)', borderRadius: 6 }} />}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{l.name}</div>
                        {l.category && <div style={{ fontSize: 11, color: 'var(--muted)' }}>{l.category}</div>}
                      </td>
                      <td>{l.brand}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{l.sku || '—'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{l.scale || '—'}</td>
                      <td>{l.ownersCount || 0}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button className="btn-ghost btn-sm" onClick={() => openEdit(l)} style={{ marginRight: 6 }}>
                          {t('admin.cat_edit', { defaultValue: 'Editar' })}
                        </button>
                        <button className="btn-ghost btn-sm" onClick={() => deleteCatalog(l)} style={{ color: '#ef4444' }}>
                          {t('admin.cat_delete', { defaultValue: 'Borrar' })}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {catPages > 1 && (
              <div className="admin-pager">
                <button
                  className="btn-ghost btn-sm"
                  disabled={catPage <= 1 || catLoading}
                  onClick={() => setCatPage((p) => Math.max(1, p - 1))}
                >
                  ← {t('admin.cat_prev', { defaultValue: 'Anterior' })}
                </button>
                <span className="admin-pager__label">
                  {t('admin.cat_page', { defaultValue: 'Página' })} {catPage} / {catPages}
                </span>
                <button
                  className="btn-ghost btn-sm"
                  disabled={catPage >= catPages || catLoading}
                  onClick={() => setCatPage((p) => Math.min(catPages, p + 1))}
                >
                  {t('admin.cat_next', { defaultValue: 'Siguiente' })} →
                </button>
              </div>
            )}
          </>
        )}

        {tab === 'publications' && (
          fetching && publications.length === 0 ? (
            <div className="admin-empty">{t('admin.pub_loading', { defaultValue: 'Cargando publicaciones…' })}</div>
          ) : publications.length === 0 ? (
            <div className="admin-empty">{t('admin.pub_empty', { defaultValue: 'No hay publicaciones pendientes de curar.' })}</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t('admin.cat_th_name', { defaultValue: 'Pieza' })}</th>
                  <th style={{ width: 130 }}>{t('admin.cat_th_brand', { defaultValue: 'Marca' })}</th>
                  <th style={{ width: 90 }}>{t('admin.cat_th_sku', { defaultValue: 'SKU' })}</th>
                  <th style={{ width: 80 }}>{t('admin.cat_th_scale', { defaultValue: 'Escala' })}</th>
                  <th style={{ width: 260 }}></th>
                </tr>
              </thead>
              <tbody>
                {publications.map((p) => (
                  <tr key={p._id}>
                    <td><div style={{ fontWeight: 600 }}>{p.name}</div></td>
                    <td>{p.brand}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.sku || '—'}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{p.scale || '—'}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button className="btn-ghost btn-sm" onClick={() => openEditPub(p)} style={{ marginRight: 6 }}>
                        {t('admin.cat_edit', { defaultValue: 'Editar' })}
                      </button>
                      <button className="btn-ghost btn-sm" onClick={() => deletePublication(p)} style={{ marginRight: 6, color: '#ef4444' }}>
                        {t('admin.cat_delete', { defaultValue: 'Borrar' })}
                      </button>
                      <button className="btn-teal btn-sm" onClick={() => openPromote(p)}>
                        + {t('admin.pub_to_catalog', { defaultValue: 'Catálogo' })}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {tab === 'sellers' && (
          sellerReqs.length === 0 ? (
            <div className="admin-empty">No hay solicitudes de vendedor.</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Email</th>
                  <th style={{ width: 130 }}>Solicitada</th>
                  <th style={{ width: 80 }}>Ventas</th>
                  <th style={{ width: 130 }}>Estado</th>
                  <th style={{ width: 240 }}></th>
                </tr>
              </thead>
              <tbody>
                {sellerReqs.map((u) => {
                  const sales = allUsers.find((x) => x._id === u._id)?.salesCount ?? 0;
                  return (
                  <tr key={u._id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{u.username || u.name}</div>
                      {u.username && u.name && u.username !== u.name && (
                        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{u.name}</div>
                      )}
                    </td>
                    <td>{u.email || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                    <td>{u.sellerRequestedAt ? new Date(u.sellerRequestedAt).toLocaleDateString() : '—'}</td>
                    <td style={{ fontFamily: 'monospace' }}>{sales}</td>
                    <td>
                      <span className={`pill pill--${u.sellerStatus === 'approved' ? 'ok' : u.sellerStatus === 'rejected' ? 'err' : 'wait'}`}>
                        {u.sellerStatus === 'approved' ? 'Aprobado' : u.sellerStatus === 'rejected' ? 'Rechazado' : 'Pendiente'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {u.sellerStatus === 'pending' && (
                        <>
                          <button className="btn-teal btn-sm" style={{ marginRight: 6 }} onClick={() => handleApproveSeller(u._id)}>
                            Aprobar
                          </button>
                          <button className="btn-ghost btn-sm" style={{ color: '#ef4444' }} onClick={() => handleRejectSeller(u._id)}>
                            Rechazar
                          </button>
                        </>
                      )}
                      {u.sellerStatus === 'approved' && (
                        <span style={{ fontSize: 12, color: 'var(--muted)' }}>Vendedor activo</span>
                      )}
                      {u.sellerStatus === 'rejected' && (
                        <button className="btn-ghost btn-sm" onClick={() => handleApproveSeller(u._id)}>
                          Aprobar de todas formas
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}

        {tab === 'users' && (
          allUsers.length === 0 ? (
            <div className="admin-empty">No hay usuarios registrados.</div>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 56 }}></th>
                  <th>Usuario</th>
                  <th>Email</th>
                  <th style={{ width: 110 }}>Alta</th>
                  <th style={{ width: 110 }}>Baja</th>
                  <th style={{ width: 90 }}>Invitaciones</th>
                  <th style={{ width: 110 }}></th>
                  <th style={{ width: 80 }}>Logins</th>
                  <th style={{ width: 130 }}>Último ingreso</th>
                </tr>
              </thead>
              <tbody>
                {allUsers.map((u) => (
                  <tr key={u._id} style={{ opacity: u.deletedAt ? 0.55 : 1 }}>
                    <td>
                      {u.avatarThumb || u.avatar ? (
                        <img src={u.avatarThumb || u.avatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--bg)', border: '1px solid var(--line)' }} />
                      )}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{u.username || u.name}</div>
                      {u.role !== 'user' && (
                        <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em' }}>{u.role}</div>
                      )}
                    </td>
                    <td>{u.email || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                    <td>{fmtDate(u.createdAt)}</td>
                    <td>{u.deletedAt ? <span style={{ color: '#ef4444' }}>{fmtDate(u.deletedAt)}</span> : <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                    <td style={{ fontFamily: 'monospace' }}>{u.invitesLeft ?? 0}</td>
                    <td>
                      {!u.deletedAt && (
                        <button
                          className="btn-teal btn-sm"
                          onClick={() => handleGrantInvites(u)}
                          disabled={grantingId === u._id}
                          title="Otorgar 30 invitaciones más"
                        >
                          {grantingId === u._id ? '…' : '+30 inv'}
                        </button>
                      )}
                    </td>
                    <td style={{ fontFamily: 'monospace' }}>{u.loginCount || 0}</td>
                    <td style={{ fontSize: 12, color: 'var(--muted)' }}>{u.lastSeenAt ? new Date(u.lastSeenAt).toLocaleString() : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {tab === 'raffle' && (() => {
          const rondaActual = settings?.sorteoRonda || 1;
          const rondaVisible = raffleRound ?? rondaActual;
          const rondas = Array.from({ length: rondaActual }, (_, i) => i + 1);
          const enRonda = raffle.filter((p) => p.ronda === rondaVisible);
          return (
            <>
              <div className="admin-card admin-switch-row" style={{ marginBottom: 16 }}>
                <div className="admin-switch-row__text">
                  <span className="admin-switch-row__title">Sorteo {rondaActual} en curso</span>
                  <span className="admin-switch-row__hint">
                    {enRonda.length === 1
                      ? `1 participante en el sorteo ${rondaVisible}`
                      : `${enRonda.length} participantes en el sorteo ${rondaVisible}`}
                    {' · Territorio: '}
                    {(settings?.sorteoTerritorio || '').trim() || 'Todos'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {rondaActual > 1 && (
                    <select
                      value={rondaVisible}
                      onChange={(e) => setRaffleRound(Number(e.target.value))}
                      aria-label="Ver participantes del sorteo"
                      className="btn-ghost"
                    >
                      {rondas.map((r) => (
                        <option key={r} value={r}>
                          Sorteo {r}{r === rondaActual ? ' · en curso' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  <button className="btn-teal" onClick={startNewRaffleRound} disabled={savingSettings}>
                    Iniciar sorteo {rondaActual + 1}
                  </button>
                </div>
              </div>

              {enRonda.length === 0 ? (
                <div className="admin-empty">
                  {rondaVisible === rondaActual
                    ? 'Aún no hay participantes en este sorteo. Un usuario entra a la lista al cumplir las tres condiciones de la dinámica.'
                    : `El sorteo ${rondaVisible} no tuvo participantes.`}
                </div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{ width: 56 }}></th>
                      <th>Participante</th>
                      <th>Email</th>
                      <th style={{ width: 120 }}>Código</th>
                      <th style={{ width: 90 }}>Referidos</th>
                      <th style={{ width: 80 }}>Piezas</th>
                      <th style={{ width: 130 }}>Participa desde</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enRonda.map((p) => (
                      <tr key={p._id} style={{ opacity: p.deletedAt ? 0.55 : 1 }}>
                        <td>
                          {p.avatar ? (
                            <img src={p.avatar} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--bg)', border: '1px solid var(--line)' }} />
                          )}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{p.username || p.name}</div>
                          {p.deletedAt && (
                            <div style={{ fontSize: 11, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Cuenta cerrada</div>
                          )}
                        </td>
                        <td>{p.email || <span style={{ color: 'var(--muted)' }}>—</span>}</td>
                        <td style={{ fontFamily: 'monospace' }}>{p.codigoPropio || '—'}</td>
                        <td style={{ fontFamily: 'monospace' }}>{p.referidosRegistrados}</td>
                        <td style={{ fontFamily: 'monospace' }}>{p.piezasSubidas}</td>
                        <td style={{ fontSize: 12, color: 'var(--muted)' }}>{fmtDate(p.activadoAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          );
        })()}

        {tab === 'announcements' && <Announcements />}

        {tab === 'mail' && (
          !mail && mailLoading ? (
            <div className="admin-empty">Cargando registro de correos…</div>
          ) : !mail ? (
            <div className="admin-empty">No se pudo cargar el registro de correos.</div>
          ) : (
            <>
              {/* Si nadie configuró el webhook, todo queda en «pendiente» para
                  siempre. Decirlo es mejor que mostrar una tabla que parece
                  sana cuando en realidad no sabemos nada. */}
              {!mail.webhookConfigurado && (
                <div className="admin-flash admin-flash--err" style={{ marginBottom: 14 }}>
                  El webhook de Resend no está configurado (falta <code>RESEND_WEBHOOK_SECRET</code>).
                  Sin él no llegan los rebotes y todo se queda en «pendiente».
                </div>
              )}

              <div className="admin-stats" style={{ marginBottom: 14 }}>
                <div className="admin-stat"><span>Enviados</span><strong>{mail.total}</strong></div>
                <div className="admin-stat"><span>Entregados</span><strong>{mail.resumen.delivered || 0}</strong></div>
                <div className="admin-stat"><span>Rebotados</span><strong style={{ color: (mail.resumen.bounced || 0) ? '#ef4444' : undefined }}>{mail.resumen.bounced || 0}</strong></div>
                <div className="admin-stat"><span>Marcados como spam</span><strong style={{ color: (mail.resumen.complained || 0) ? '#ef4444' : undefined }}>{mail.resumen.complained || 0}</strong></div>
                <div className="admin-stat"><span>Sin confirmar</span><strong>{mail.resumen.pending || 0}</strong></div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                {[
                  ['', 'Todos'],
                  ['bounced', 'Rebotados'],
                  ['complained', 'Spam'],
                  ['delivered', 'Entregados'],
                  ['pending', 'Sin confirmar'],
                ].map(([v, label]) => (
                  <button
                    key={v || 'all'}
                    className={`admin-tab${mailFilter === v ? ' admin-tab--active' : ''}`}
                    onClick={() => setMailFilter(v)}
                  >
                    {label}
                  </button>
                ))}
                <button className="btn-ghost" style={{ marginLeft: 'auto' }} onClick={() => loadMail(mailFilter)} disabled={mailLoading}>
                  {mailLoading ? 'Actualizando…' : 'Actualizar'}
                </button>
              </div>

              {mail.logs.length === 0 ? (
                <div className="admin-empty">Sin correos en este filtro.</div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th style={{ width: 140 }}>Fecha</th>
                      <th style={{ width: 150 }}>Tipo</th>
                      <th>Destinatario</th>
                      <th style={{ width: 150 }}>Entrega</th>
                      <th>Detalle</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mail.logs.map((l) => {
                      const d = l.status === 'failed'
                        ? { label: 'No se envió', cls: 'pill--err' }
                        : ({
                          delivered:  { label: 'Entregado',   cls: 'pill--ok' },
                          bounced:    { label: 'Rebotado',    cls: 'pill--err' },
                          complained: { label: 'Spam',        cls: 'pill--err' },
                          delayed:    { label: 'Reintentando', cls: 'pill--wait' },
                          failed:     { label: 'Falló',       cls: 'pill--err' },
                        }[l.delivery] || { label: 'Sin confirmar', cls: 'pill--wait' });
                      return (
                        <tr key={l._id}>
                          <td style={{ fontSize: 12 }}>{new Date(l.createdAt).toLocaleString()}</td>
                          <td style={{ fontSize: 12, fontFamily: 'monospace', color: 'var(--muted)' }}>{l.type}</td>
                          <td>
                            <div>{l.to}</div>
                            <div style={{ fontSize: 11, color: 'var(--muted)' }}>{l.subject}</div>
                          </td>
                          <td>
                            <span className={`pill ${d.cls}`}>{d.label}</span>
                            {l.openedAt && <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 3 }}>abierto</div>}
                          </td>
                          <td style={{ fontSize: 11, color: 'var(--muted)' }}>
                            {l.error || l.deliveryDetail || <span style={{ opacity: .5 }}>—</span>}
                            {l.resendId && (
                              <div style={{ fontFamily: 'monospace', fontSize: 10, opacity: .6, marginTop: 3 }}>{l.resendId}</div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </>
          )
        )}

        {tab === 'panel' && (
          !settings ? (
            <div className="admin-empty">Cargando configuración…</div>
          ) : (() => {
            const cfgSource = settings.homeSource || 'catalog';
            const salesOn = settings.homeShowSales === true;
            const raffleOn = settings.growthRaffleEnabled !== false;
            const chipOn = settings.memberChipEnabled !== false;
            const commissionOn = settings.marketplaceCommissionEnabled === true;
            const commissionPct = settings.marketplaceCommissionPct ?? 10;
            // El meter refleja el borrador en edición, no lo persistido, para
            // previsualizar la ventana antes de guardarla.
            const { start: cfgStart, end: cfgEnd } = winPos;
            const count = Math.max(0, cfgEnd - cfgStart);
            const isPubs = cfgSource === 'publications';
            const sourceTotal = settings.counts?.[cfgSource] ?? 0;
            const maxPos = settings.limits?.maxPos ?? 200000;
            const maxWindow = settings.limits?.maxWindow ?? 1000;
            const meterMax = Math.max(sourceTotal, cfgEnd, 10);
            const pctA = Math.min(100, (cfgStart / meterMax) * 100);
            const pctB = Math.min(100, (cfgEnd / meterMax) * 100);
            const shown = Math.max(0, Math.min(cfgEnd, sourceTotal) - Math.min(cfgStart, sourceTotal));
            return (
              <section className="admin-panel">
                <h3 className="admin-panel__title">Almacenamiento</h3>

                {/* ── Meter: ventana de piezas a mostrar (inicio–fin) ── */}
                <div className="admin-card">
                  <div className="admin-meter__head">
                    <span className="admin-meter__label">Mostrar por defecto</span>
                    {savingSettings && <span className="admin-meter__saving">Guardando…</span>}
                  </div>
                  <div className="admin-meter__value">
                    <strong>{count}</strong> Piezas
                  </div>

                  {/* Posiciones editables: desde–hasta dentro de la fuente. */}
                  <div className="admin-meter__fields">
                    <label className="admin-meter__field">
                      <span>Desde la pieza</span>
                      <input
                        type="number"
                        className="light-input admin-meter__num"
                        min={1}
                        max={maxPos}
                        value={winEdit.from}
                        onChange={(e) => setWinField('from')(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && winDirty) saveWindow(); }}
                      />
                    </label>
                    <span className="admin-meter__dash" aria-hidden="true">–</span>
                    <label className="admin-meter__field">
                      <span>Hasta la pieza</span>
                      <input
                        type="number"
                        className="light-input admin-meter__num"
                        min={1}
                        max={maxPos}
                        value={winEdit.to}
                        onChange={(e) => setWinField('to')(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && winDirty) saveWindow(); }}
                      />
                    </label>
                  </div>

                  <div className="admin-meter__dual" style={{ '--a': `${pctA}%`, '--b': `${pctB}%` }}>
                    <input
                      type="range"
                      className="admin-meter__range admin-meter__range--dual"
                      aria-label="Inicio de la ventana"
                      min={0}
                      max={meterMax}
                      value={cfgStart}
                      onChange={(e) => onStartInput(e.target.value)}
                    />
                    <input
                      type="range"
                      className="admin-meter__range admin-meter__range--dual"
                      aria-label="Final de la ventana"
                      min={0}
                      max={meterMax}
                      value={cfgEnd}
                      onChange={(e) => onEndInput(e.target.value)}
                    />
                  </div>
                  <div className="admin-meter__foot">
                    Mostrando {shown} (posiciones {cfgStart + 1}–{Math.min(cfgEnd, sourceTotal)}) de {sourceTotal} piezas
                    con imagen disponibles en <strong>{isPubs ? 'Publicaciones' : 'Catálogo'}</strong>
                  </div>
                  {count > maxWindow && (
                    <div className="admin-meter__warn">
                      La ventana se recortará a {maxWindow} piezas al guardar: es lo máximo
                      que el Home pinta de una sola vez.
                    </div>
                  )}

                  {/* Barra de guardado: solo aparece con cambios sin guardar. */}
                  {winDirty && (
                    <div className="admin-meter__actions">
                      <button className="btn-ghost btn-sm" onClick={discardWindow} disabled={savingSettings}>
                        Descartar
                      </button>
                      <button className="btn-teal btn-sm" onClick={saveWindow} disabled={savingSettings}>
                        {savingSettings ? 'Guardando…' : 'Guardar'}
                      </button>
                    </div>
                  )}
                </div>

                {/* ── Toggle: piezas en venta con prioridad en el Home ── */}
                <div className="admin-card admin-switch-row">
                  <div className="admin-switch-row__text">
                    <span className="admin-switch-row__title">Piezas en venta en el Home</span>
                    <span className="admin-switch-row__hint">
                      {salesOn
                        ? `ON · El Home muestra primero las ${settings.counts?.sales ?? 0} piezas en venta de todos los usuarios y después la ventana de ${isPubs ? 'Publicaciones' : 'Catálogo'}.`
                        : 'OFF · El Home muestra solo la ventana configurada.'}
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={salesOn}
                    aria-label="Piezas en venta con prioridad en el Home"
                    className={`admin-switch${salesOn ? ' admin-switch--on' : ''}`}
                    onClick={toggleHomeSales}
                    disabled={savingSettings}
                  >
                    <span className="admin-switch__knob" />
                  </button>
                </div>

                {/* ── Toggle: fuente del Home ── */}
                <div className="admin-card admin-switch-row">
                  <div className="admin-switch-row__text">
                    <span className="admin-switch-row__title">Admin piezas mostrar en el Home</span>
                    <span className="admin-switch-row__hint">
                      {isPubs
                        ? 'ON · El Home muestra solo piezas de las publicaciones.'
                        : 'OFF · El Home muestra solo piezas del catálogo.'}
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isPubs}
                    aria-label="Fuente de piezas del Home"
                    className={`admin-switch${isPubs ? ' admin-switch--on' : ''}`}
                    onClick={toggleHomeSource}
                    disabled={savingSettings}
                  >
                    <span className="admin-switch__knob" />
                  </button>
                </div>

                {/* ── Toggle: dinámica de growth (sorteo) ── */}
                <div className="admin-card admin-switch-row">
                  <div className="admin-switch-row__text">
                    <span className="admin-switch-row__title">Dinámica de growth · Sorteo</span>
                    <span className="admin-switch-row__hint">
                      {raffleOn
                        ? 'ON · Los perfiles muestran el botón «Participar en el sorteo».'
                        : 'OFF · Los perfiles muestran el botón «Invitar a un amigo» (usa la cuota de invitaciones del usuario).'}
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={raffleOn}
                    aria-label="Dinámica de growth del sorteo"
                    className={`admin-switch${raffleOn ? ' admin-switch--on' : ''}`}
                    onClick={toggleGrowthRaffle}
                    disabled={savingSettings}
                  >
                    <span className="admin-switch__knob" />
                  </button>
                </div>

                {/* ── Toggle: insignia de número de miembro ── */}
                <div className="admin-card admin-switch-row">
                  <div className="admin-switch-row__text">
                    <span className="admin-switch-row__title">{t('admin.member_chip_title')}</span>
                    <span className="admin-switch-row__hint">
                      {chipOn ? t('admin.member_chip_on') : t('admin.member_chip_off')}
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={chipOn}
                    aria-label={t('admin.member_chip_aria')}
                    className={`admin-switch${chipOn ? ' admin-switch--on' : ''}`}
                    onClick={toggleMemberChip}
                    disabled={savingSettings}
                  >
                    <span className="admin-switch__knob" />
                  </button>
                </div>

                {/* ── Comisión de Troly (Stripe Connect) ── */}
                <div className="admin-card admin-switch-row">
                  <div className="admin-switch-row__text">
                    <span className="admin-switch-row__title">Comisión Troly en ventas</span>
                    <span className="admin-switch-row__hint">
                      {commissionOn
                        ? `ON · Troly retiene ${commissionPct}% de cada venta cobrada con tarjeta; el vendedor recibe el ${Math.round((100 - commissionPct) * 100) / 100}%.`
                        : 'OFF · El vendedor recibe el 100%. Ojo: la tarifa de procesamiento de Stripe la absorbe Troly.'}
                      {' '}Aplica solo a compras nuevas.
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <input
                        type="number"
                        min={0}
                        max={50}
                        step={0.5}
                        key={commissionPct}
                        defaultValue={commissionPct}
                        aria-label="Porcentaje de comisión"
                        className="light-input"
                        style={{ width: 76, fontFamily: 'var(--troly-font-mono)' }}
                        disabled={savingSettings || !commissionOn}
                        onBlur={(e) => commitCommissionPct(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                      />
                      <span aria-hidden="true">%</span>
                    </label>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={commissionOn}
                      aria-label="Cobrar comisión de Troly"
                      className={`admin-switch${commissionOn ? ' admin-switch--on' : ''}`}
                      onClick={toggleCommission}
                      disabled={savingSettings}
                    >
                      <span className="admin-switch__knob" />
                    </button>
                  </div>
                </div>

                {/* ── Territorio del sorteo ── */}
                <div className="admin-card admin-switch-row">
                  <div className="admin-switch-row__text">
                    <span className="admin-switch-row__title">Territorio del sorteo</span>
                    <span className="admin-switch-row__hint">
                      Solo participan usuarios cuya ubicación contenga este territorio
                      (ej. «México»). Déjalo vacío para un sorteo sin restricción territorial.
                    </span>
                  </div>
                  <input
                    type="text"
                    key={settings.sorteoTerritorio ?? 'México'}
                    defaultValue={settings.sorteoTerritorio ?? 'México'}
                    placeholder="Todos los territorios"
                    aria-label="Territorio del sorteo"
                    className="light-input"
                    style={{ maxWidth: 220 }}
                    disabled={savingSettings}
                    onBlur={(e) => commitTerritorio(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  />
                </div>

                {/* ── Cierre de inscripciones ── */}
                <div className="admin-card admin-switch-row">
                  <div className="admin-switch-row__text">
                    <span className="admin-switch-row__title">Cierre de inscripciones</span>
                    <span className="admin-switch-row__hint">
                      Pasada esta fecha no entra nadie nuevo al sorteo ni se emiten
                      números adicionales; los ya asignados no se tocan. Debe coincidir
                      con la fecha que anuncian las bases. Vacío = sin cierre.
                      {settings.sorteoCerrado && ' · INSCRIPCIONES CERRADAS'}
                    </span>
                  </div>
                  <input
                    type="datetime-local"
                    key={settings.sorteoCierraAt ?? 'sin-cierre'}
                    defaultValue={toLocalInput(settings.sorteoCierraAt)}
                    aria-label="Cierre de inscripciones del sorteo"
                    className="light-input"
                    style={{ maxWidth: 220 }}
                    disabled={savingSettings}
                    onBlur={(e) => commitCierre(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  />
                </div>

                {/* ── Mantenimiento del catálogo ──
                    Estos recálculos recorren las 14k+ piezas, así que ya no
                    corren solos en cada carga del panel: se disparan a mano
                    cuando algo se editó fuera de la app (scripts de import o
                    cambios directos en Mongo). */}
                <div className="admin-card admin-switch-row">
                  <div className="admin-switch-row__text">
                    <span className="admin-switch-row__title">Mantenimiento del catálogo</span>
                    <span className="admin-switch-row__hint">
                      «Dueños» recalcula cuántos coleccionistas tienen cada pieza.
                      «Feed» recalcula qué piezas tienen foto y su orden en el Home.
                      Corre cualquiera de los dos después de un script de import.
                    </span>
                  </div>
                  <div className="admin-meter__actions">
                    <button
                      className="btn-ghost btn-sm"
                      disabled={!!maintaining}
                      onClick={() => runMaintenance('recompute-owners', 'Dueños')}
                    >
                      {maintaining === 'recompute-owners' ? 'Recalculando…' : 'Dueños'}
                    </button>
                    <button
                      className="btn-ghost btn-sm"
                      disabled={!!maintaining}
                      onClick={() => runMaintenance('recompute-feed', 'Feed')}
                    >
                      {maintaining === 'recompute-feed' ? 'Recalculando…' : 'Feed'}
                    </button>
                  </div>
                </div>
              </section>
            );
          })()
        )}

        {tab === 'metrics' && (
          !metrics ? (
            <div className="admin-empty">Cargando métricas…</div>
          ) : (
            <div className="admin-metrics">
              <section>
                <h3 className="admin-metrics__h">Engagement</h3>
                <div className="admin-stats">
                  <div className="admin-stat"><span>DAU · 24h</span><strong>{metrics.engagement.dau}</strong></div>
                  <div className="admin-stat"><span>WAU · 7d</span><strong>{metrics.engagement.wau}</strong></div>
                  <div className="admin-stat"><span>MAU · 30d</span><strong>{metrics.engagement.mau}</strong></div>
                  <div className="admin-stat"><span>Activos / Total</span><strong>{metrics.users.active} / {metrics.users.total}</strong></div>
                </div>
              </section>

              <section>
                <h3 className="admin-metrics__h">Funnel waitlist → compra</h3>
                <div className="admin-stats">
                  <div className="admin-stat"><span>Waitlist</span><strong>{metrics.funnel.waitlist}</strong></div>
                  <div className="admin-stat"><span>Invitaciones enviadas</span><strong>{metrics.funnel.invitationsSent}</strong></div>
                  <div className="admin-stat"><span>Invitaciones usadas</span><strong>{metrics.funnel.invitationsUsed}</strong></div>
                  <div className="admin-stat"><span>1ª compra (buyers)</span><strong>{metrics.funnel.firstPurchaseBuyers}</strong></div>
                </div>
              </section>

              <section>
                <h3 className="admin-metrics__h">Catálogo y publicaciones</h3>
                <div className="admin-stats">
                  <div className="admin-stat"><span>Listings catálogo</span><strong>{metrics.catalog.listings}</strong></div>
                  <div className="admin-stat"><span>Publicaciones total</span><strong>{metrics.catalog.publications}</strong></div>
                  <div className="admin-stat"><span>Pub · 24h</span><strong>{metrics.catalog.publications24h}</strong></div>
                  <div className="admin-stat"><span>Pub · 7d</span><strong>{metrics.catalog.publications7d}</strong></div>
                </div>
              </section>

              <section>
                <h3 className="admin-metrics__h">Ventas</h3>
                <div className="admin-stats">
                  <div className="admin-stat"><span>Órdenes 24h</span><strong>{metrics.sales.orders24h}</strong></div>
                  <div className="admin-stat"><span>Órdenes 7d</span><strong>{metrics.sales.orders7d}</strong></div>
                  <div className="admin-stat"><span>Órdenes total</span><strong>{metrics.sales.ordersTotal}</strong></div>
                  <div className="admin-stat">
                    <span>GMV ≈ USD</span>
                    <strong>{metrics.sales.gmvUsd != null ? `$${fmtMoney(metrics.sales.gmvUsd)}` : '—'}</strong>
                  </div>
                </div>
                {metrics.sales.gmvByCurrency.length > 0 && (
                  <table className="admin-table" style={{ marginTop: 12 }}>
                    <thead>
                      <tr>
                        <th>Divisa</th>
                        <th style={{ width: 120 }}>Órdenes</th>
                        <th style={{ width: 160 }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {metrics.sales.gmvByCurrency.map((row) => (
                        <tr key={row.currency}>
                          <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{row.currency}</td>
                          <td>{row.orders}</td>
                          <td>{fmtMoney(row.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section>
                <h3 className="admin-metrics__h">Vendedores</h3>
                <div className="admin-stats">
                  <div className="admin-stat"><span>Aprobados</span><strong>{metrics.users.sellersApproved}</strong></div>
                  <div className="admin-stat"><span>Pendientes</span><strong>{metrics.users.sellersPending}</strong></div>
                  <div className="admin-stat"><span>Bajas</span><strong>{metrics.users.deleted}</strong></div>
                  <div className="admin-stat" style={{ gridColumn: 'span 1' }}>
                    <span>Generado</span>
                    <strong style={{ fontSize: 13 }}>{new Date(metrics.generatedAt).toLocaleTimeString()}</strong>
                  </div>
                </div>
              </section>

              <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
                Para tráfico web (páginas vistas, fuente, retención por cohortes) usa Google Analytics.
                Estas métricas son de negocio (BD).
              </p>
            </div>
          )
        )}

      </div>

      {/* ── Catalog editor modal ── */}
      {editing && (
        <div className="modal-overlay" onClick={closeEditor}>
          <div className="modal-card" style={{ maxWidth: 640 }} onClick={(e) => e.stopPropagation()}>
            <h3>{
              editMode === 'promote' ? t('admin.cat_promote_title', { defaultValue: 'Agregar al catálogo' })
              : editMode === 'publication' ? t('admin.pub_edit_title', { defaultValue: 'Editar publicación' })
              : editing === 'new' ? t('admin.cat_new', { defaultValue: 'Nueva pieza' })
              : t('admin.cat_edit_title', { defaultValue: 'Editar pieza' })
            }</h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14 }}>
              <label style={{ gridColumn: '1 / -1' }}>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{t('upload.name_label')}</span>
                <input className="light-input" value={form.name} onChange={setField('name')} required />
              </label>
              <label>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{t('upload.brand_label')}</span>
                <select className="light-select" value={form.brand} onChange={setField('brand')} required>
                  <option value="">—</option>
                  {options.brands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>
              <label>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{t('upload.category_label')}</span>
                <select className="light-select" value={form.category} onChange={setField('category')} required>
                  <option value="">—</option>
                  {options.categories.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{t('upload.material_label')}</span>
                <select className="light-select" value={form.material} onChange={setField('material')}>
                  <option value="">—</option>
                  {options.materials.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </label>
              <label>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{t('upload.scale_label')}</span>
                <select className="light-select" value={form.scale} onChange={setField('scale')}>
                  <option value="">—</option>
                  {options.scales.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{t('upload.sku_label')}</span>
                <input className="light-input" value={form.sku} onChange={setField('sku')} />
              </label>
              <label>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>Toy Number</span>
                <input className="light-input" value={form.toyNumber} onChange={setField('toyNumber')} />
              </label>
              <label>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{t('upload.year_label')}</span>
                <input className="light-input" type="number" value={form.launchYear} onChange={setField('launchYear')} />
              </label>
              {editMode !== 'publication' && (
                <label>
                  <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>
                    {t('admin.cat_market_value', { defaultValue: 'Valor de referencia (USD)' })}
                  </span>
                  <input className="light-input" type="number" min="0" value={form.marketValueAmount} onChange={setField('marketValueAmount')} />
                </label>
              )}
              {editMode !== 'publication' && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, alignSelf: 'end', paddingBottom: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!form.carded}
                    onChange={(e) => setForm((f) => ({ ...f, carded: e.target.checked }))}
                  />
                  <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                    {t('admin.cat_carded', { defaultValue: 'Foto en blíster (carded) · prioridad en el Home' })}
                  </span>
                </label>
              )}
              <label style={{ gridColumn: '1 / -1' }}>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{t('upload.desc_label')}</span>
                <textarea className="light-input" style={{ minHeight: 70 }} value={form.description} onChange={setField('description')} />
              </label>
              <label style={{ gridColumn: '1 / -1' }}>
                <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)', marginBottom: 4 }}>{t('admin.cat_image', { defaultValue: 'Imagen de referencia (opcional)' })}</span>
                <input type="file" accept="image/*" onChange={(e) => { setImgFile(e.target.files?.[0] || null); setRemoveImg(false); }} />
                {editMode === 'promote' && !imgFile && (
                  <div style={{ marginTop: 6, fontSize: 11, color: 'var(--muted)' }}>
                    {t('admin.cat_promote_image_hint', { defaultValue: 'Si no subes una imagen, se usará la foto de la publicación.' })}
                  </div>
                )}
                {editing !== 'new' && editing.image && !imgFile && !removeImg && (
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                    <img src={editing.image} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 6 }} />
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      style={{ color: '#ef4444' }}
                      onClick={() => setRemoveImg(true)}
                    >
                      {t('admin.cat_remove_image', { defaultValue: 'Quitar imagen' })}
                    </button>
                  </div>
                )}
                {editing !== 'new' && removeImg && !imgFile && (
                  <div style={{ marginTop: 8, fontSize: 12, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 10 }}>
                    {t('admin.cat_image_will_be_removed', { defaultValue: 'La imagen actual se eliminará al guardar.' })}
                    <button
                      type="button"
                      className="btn-ghost btn-sm"
                      onClick={() => setRemoveImg(false)}
                    >
                      {t('admin.cat_undo', { defaultValue: 'Deshacer' })}
                    </button>
                  </div>
                )}
              </label>
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
              <button className="btn-ghost" onClick={closeEditor} disabled={savingCat}>{t('admin.cat_cancel', { defaultValue: 'Cancelar' })}</button>
              <button className="btn-teal" onClick={saveCatalog} disabled={savingCat}>
                {savingCat ? t('admin.cat_saving', { defaultValue: 'Guardando…' }) : t('admin.cat_save', { defaultValue: 'Guardar' })}
              </button>
            </div>
            <button className="modal-close" onClick={closeEditor}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
}
